// HomeScreen — 雀魂参考の明るいホーム (owner decision 2026-06-17). Character-forward, fit-to-viewport.
// Maps flow.md メイン画面 (l.105–110): 段位戦 / 友人戦 / 福引 / イベント / 役解説 / 設定 + 段位プログレス
// + 圓・招福札残数. 工房 is demoted into the feature dock (developer surface, not a player mode).
//
// Data note: there is NO account/economy/progression store yet, so profile values are placeholders
// ('ゲスト' / '—'). When the account foundation lands, real 段位/圓/札 + the equipped 立ち絵 flow in here.
// The centerpiece uses a procedural placeholder 立ち絵 (portraitArt.tsx); real art swaps via art.ref.
import './home.css';
import { allPortraits } from '../content/portraits';
import { ProceduralPortrait } from '../content/portraitArt';

export interface HomeProfile {
  name: string;
  emblem: string; // single char shown in the 段位 emblem
  rankLabel: string; // e.g. '雀士 ②' or '未参加'
  progress: number; // 0..1 昇段 progress
  yen: string; // formatted 圓 balance, or '—'
  fuda: string; // 招福札 count, or '—'
}

// No account store yet → honest guest placeholder. flow.md: unlogged users still reach ソロ.
const GUEST: HomeProfile = { name: 'ゲスト', emblem: '雀', rankLabel: '未参加', progress: 0, yen: '—', fuda: '—' };

// placeholder centerpiece until an equipped 立ち絵 exists (procedural art, swappable).
const FEATURED = allPortraits()[0]!;

function Gear() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="var(--h-ink)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.5v2.4M12 19.1v2.4M21.5 12h-2.4M4.9 12H2.5M18.7 5.3l-1.7 1.7M7 17l-1.7 1.7M18.7 18.7L17 17M7 7 5.3 5.3" />
    </svg>
  );
}

export interface HomeScreenProps {
  onRanked(): void;
  onFriend(): void;
  onSolo(): void;
  onFukubiki(): void;
  onEvents(): void;
  onYakuGuide(): void;
  onStudio(): void;
  onSettings(): void;
  profile?: HomeProfile;
  /** The equipped 立ち絵 image src (real note/booth art). When absent, a procedural placeholder is shown. */
  featuredArtSrc?: string;
}

export function HomeScreen({
  onRanked,
  onFriend,
  onSolo,
  onFukubiki,
  onEvents,
  onYakuGuide,
  onStudio,
  onSettings,
  profile = GUEST,
  featuredArtSrc,
}: HomeScreenProps) {
  return (
    <div className="home">
      <div className="home-scene">
        <div className="home-rays" />
        <div className="home-tatami" />
      </div>

      {/* top bar: 段位 + progress · 圓 · 招福札 · 設定 */}
      <div className="home-top">
        <div className="home-rank">
          <div className="home-emblem">{profile.emblem}</div>
          <div className="home-who">
            <div className="home-name">{profile.name} · {profile.rankLabel}</div>
            <div className="home-bar"><i style={{ width: `${Math.round(profile.progress * 100)}%` }} /></div>
          </div>
        </div>
        <div className="home-spacer" />
        <span className="home-pill"><span className="home-ic">圓</span>{profile.yen}</span>
        <span className="home-pill"><span className="home-ic fuda">札</span>{profile.fuda}</span>
        <button type="button" className="home-gear" aria-label="設定" onClick={onSettings}><Gear /></button>
      </div>

      {/* event ribbon (→ イベント) */}
      <button type="button" className="home-event" onClick={onEvents}>
        <b>イベント</b> 準備中
      </button>

      {/* character stage */}
      <div className="home-stage">
        <div className="home-halo" />
        <div className="home-ring" />
        {featuredArtSrc ? (
          <img className="home-tachie" src={featuredArtSrc} alt="立ち絵" />
        ) : (
          <>
            <div className="home-fig"><ProceduralPortrait portrait={FEATURED} /></div>
            <div className="home-tag">立ち絵 準備中</div>
          </>
        )}
      </div>

      {/* primary + secondary CTAs */}
      <div className="home-cta">
        <button type="button" className="home-play" onClick={onRanked}>
          段 位 戦<span className="home-sweep" />
        </button>
        <div className="home-subrow">
          <button type="button" className="home-sub" onClick={onFriend}>友人戦</button>
          <button type="button" className="home-sub alt" onClick={onSolo}>ソロ</button>
        </div>
      </div>

      {/* feature dock — 工房 demoted here */}
      <div className="home-dock">
        <button type="button" className="home-feat hot" onClick={onFukubiki}><span className="home-fi">福</span><span>福引</span></button>
        <button type="button" className="home-feat" onClick={onEvents}><span className="home-fi">祭</span><span>イベント</span></button>
        <button type="button" className="home-feat" onClick={onYakuGuide}><span className="home-fi">役</span><span>役解説</span></button>
        <button type="button" className="home-feat" onClick={onStudio}><span className="home-fi">工</span><span>工房</span></button>
      </div>
    </div>
  );
}
